import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { featureFlagsApi } from '../services/api';
import { 
  PageHeader, 
  LoadingSpinner, 
  EmptyState, 
  ListContainer, 
  Badge,
  ErrorMessage,
  Card,
  CardHeader,
  CardBody,
  Button
} from '../components/common';
import { Flag, Users, Building, Eye, ToggleRight, ToggleLeft } from 'lucide-react';

const FeatureTypeList = () => {
  const navigate = useNavigate();
  const [featureTypes, setFeatureTypes] = useState([]);
  const [featureStats, setFeatureStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchFeatureTypesWithStats();
  }, []);

  const fetchFeatureTypesWithStats = async () => {
    try {
      setLoading(true);
      
      // Get all available feature types from the dedicated endpoint
      const typesResponse = await featureFlagsApi.getTypes();
      const typesData = typesResponse.data || [];
      
      let formattedTypes = [];
      
      if (typesData.length > 0 && typeof typesData[0] === 'object') {
        // If the API returns objects with properties
        formattedTypes = typesData.map(typeObj => ({
          value: typeObj.value || typeObj.id || typeObj,
          name: typeObj.name || typeObj.displayName || `Feature Type ${typeObj.value || typeObj.id || typeObj}`
        }));
      } else {
        // If the API returns simple values
        const uniqueTypes = [...new Set(typesData)];
        formattedTypes = uniqueTypes.map(typeValue => ({
          value: typeValue,
          name: `Feature Type ${typeValue}`
        }));
      }
      
      // Sort by value
      formattedTypes.sort((a, b) => a.value - b.value);
      setFeatureTypes(formattedTypes);

      // Fetch stats for each feature type
      const statsPromises = formattedTypes.map(async (featureType) => {
        try {
          // Get all flags for this feature type
          const flagsResponse = await featureFlagsApi.list({
            featureType: featureType.value,
            pageSize: 1000 // Get all flags for this type
          });
          
          const flags = flagsResponse.data.items || [];
          const enabledFlags = flags.filter(flag => flag.isEnabled);
          const disabledFlags = flags.filter(flag => !flag.isEnabled);
          const uniqueUsers = new Set(flags.map(flag => flag.userId)).size;
          const uniqueBuildings = new Set(flags.filter(flag => flag.buildingId).map(flag => flag.buildingId)).size;

          return {
            featureType: featureType.value,
            totalFlags: flags.length,
            enabledFlags: enabledFlags.length,
            disabledFlags: disabledFlags.length,
            uniqueUsers,
            uniqueBuildings
          };
        } catch (err) {
          console.error(`Failed to fetch stats for feature type ${featureType.value}:`, err);
          return {
            featureType: featureType.value,
            totalFlags: 0,
            enabledFlags: 0,
            disabledFlags: 0,
            uniqueUsers: 0,
            uniqueBuildings: 0
          };
        }
      });

      const stats = await Promise.all(statsPromises);
      const statsMap = {};
      stats.forEach(stat => {
        statsMap[stat.featureType] = stat;
      });
      
      setFeatureStats(statsMap);
    } catch (err) {
      setError(err.message || 'Failed to fetch feature types');
    } finally {
      setLoading(false);
    }
  };

  const getFeatureTypeName = (featureType) => {
    const type = featureTypes.find(t => t.value === featureType);
    return type ? type.name : `Feature Type ${featureType}`;
  };

  const handleViewFeatureDetail = (featureType) => {
    navigate(`/feature-flags/types/${featureType}`);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feature Types"
        subtitle="Overview of all available feature types and their usage"
        icon={Flag}
        actions={
          <Button
            variant="secondary"
            onClick={() => navigate('/feature-flags')}
          >
            Back to Feature Flags
          </Button>
        }
      />

      {error && <ErrorMessage message={error} />}

      <ListContainer>
        {featureTypes.length === 0 ? (
          <EmptyState
            title="No feature types found"
            description="No feature types are currently available."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureTypes.map((featureType) => {
              const stats = featureStats[featureType.value] || {
                totalFlags: 0,
                enabledFlags: 0,
                disabledFlags: 0,
                uniqueUsers: 0,
                uniqueBuildings: 0
              };

              return (
                <Card 
                  key={featureType.value} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleViewFeatureDetail(featureType.value)}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2 bg-primary-100 rounded-lg mr-3">
                          <Flag size={20} className="text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {featureType.name}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Type {featureType.value}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="info" className="text-xs">
                          {stats.totalFlags} flags
                        </Badge>
                        <Eye size={16} className="text-gray-400" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-4">
                      {/* Stats Summary */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-900">
                            {stats.totalFlags}
                          </div>
                          <div className="text-sm text-gray-500">Total Flags</div>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {stats.enabledFlags}
                          </div>
                          <div className="text-sm text-gray-500">Enabled</div>
                        </div>
                      </div>

                      {/* Usage Information */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Users size={16} className="mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Users</span>
                          </div>
                          <Badge variant="secondary">
                            {stats.uniqueUsers}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Building size={16} className="mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Buildings</span>
                          </div>
                          <Badge variant="secondary">
                            {stats.uniqueBuildings}
                          </Badge>
                        </div>
                      </div>

                      {/* Enabled/Disabled Ratio */}
                      {stats.totalFlags > 0 && (
                        <div>
                          <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>Enabled Rate</span>
                            <span>{Math.round((stats.enabledFlags / stats.totalFlags) * 100)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{
                                width: `${(stats.enabledFlags / stats.totalFlags) * 100}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      )}

                      {/* Action Button */}
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent card click
                          handleViewFeatureDetail(featureType.value);
                        }}
                        className="w-full mt-4"
                      >
                        View Details
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        )}
      </ListContainer>
    </div>
  );
};

export default FeatureTypeList;